import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Slider,
} from "@mhome/ui";
import { Close } from "@mui/icons-material";
import { kelvinToRGB, getDisplayColor } from "../common/color-util";
import { spacingToPx } from "../../lib/utils";

// localStorage utilities for favorite colors
const FAVORITE_COLORS_KEY = "colorDialog1_favoriteColors";

const getFavoriteColors = () => {
  try {
    const stored = localStorage.getItem(FAVORITE_COLORS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("Error reading favorite colors from localStorage:", error);
    return [];
  }
};

const saveFavoriteColor = (color, mode) => {
  try {
    const favorites = getFavoriteColors();
    const colorEntry = { color, mode, timestamp: Date.now() };

    // Remove if already exists
    const filtered = favorites.filter(
      (fav) => !(fav.color === color && fav.mode === mode)
    );

    // Add to beginning
    filtered.unshift(colorEntry);

    // Keep only last 12
    const updatedFavorites = filtered.slice(0, 12);

    localStorage.setItem(FAVORITE_COLORS_KEY, JSON.stringify(updatedFavorites));
  } catch (error) {
    console.error("Error saving favorite color to localStorage:", error);
  }
};

const ColorDialog1 = ({
  open,
  onClose,
  colorRgb,
  colorTemp,
  onRgbUpdate,
  onTempUpdate,
  minTemp = 2700,
  maxTemp = 6500,
  current = "rgb",
  disabled = false,
  showControls = "all", // "all", "rgb", "temperature"
  brightness = 50, // 0-100 brightness value
}) => {
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [temperature, setTemperature] = useState(Number(colorTemp) || 2700);
  const [mode, setMode] = useState(current);
  const [favoriteColors, setFavoriteColors] = useState([]);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    setMode(current);
  }, [current]);

  useEffect(() => {
    setTemperature(Number(colorTemp) || 2700);
  }, [colorTemp]);

  useEffect(() => {
    // Load favorite colors when dialog opens
    if (open) {
      setFavoriteColors(getFavoriteColors());
    }
  }, [open]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (colorRgb) {
      // Convert hex to HSL
      const hex = colorRgb.startsWith("#") ? colorRgb.substring(1) : colorRgb;
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h,
        s,
        l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }

      setHue(Math.round(h * 360));
      setSaturation(Math.round(s * 100));
    }
  }, [colorRgb]);

  const updateRgbColor = (newHue, newSaturation) => {
    if (disabled) return;
    // Convert HSL to Hex
    const h = newHue / 360;
    const s = newSaturation / 100;
    const l = 0.5;

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    const hexColor = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    setMode("rgb");
    onRgbUpdate && onRgbUpdate(hexColor.substring(1));

    // Debounced save to favorites to prevent duplicates
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveFavoriteColor(hexColor.substring(1), "rgb");
      setFavoriteColors(getFavoriteColors());
    }, 300); // 300ms debounce
  };

  const handleHueChange = (event, newValue) => {
    if (disabled) return;
    setHue(newValue);
  };

  const handleHueChangeCommitted = (event, newValue) => {
    if (disabled) return;
    updateRgbColor(newValue, saturation);
  };

  const handleSaturationChange = (event, newValue) => {
    if (disabled) return;
    setSaturation(newValue);
  };

  const handleSaturationChangeCommitted = (event, newValue) => {
    if (disabled) return;
    updateRgbColor(hue, newValue);
  };

  const handleTemperatureChange = (event, newValue) => {
    if (disabled) return;
    setTemperature(newValue);
  };

  const handleTemperatureChangeCommitted = (event, newValue) => {
    if (disabled) return;
    setMode("temperature");
    const syntheticEvent = {
      preventDefault: () => {},
      stopPropagation: () => {},
      target: {
        value: newValue,
      },
    };
    onTempUpdate && onTempUpdate(syntheticEvent, newValue);

    // Debounced save to favorites to prevent duplicates
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveFavoriteColor(newValue.toString(), "temperature");
      setFavoriteColors(getFavoriteColors());
    }, 300); // 300ms debounce
  };

  const handleFavoriteColorClick = (favorite) => {
    if (disabled) return;

    if (favorite.mode === "rgb") {
      // Handle RGB color
      const hexColor = favorite.color.startsWith("#")
        ? favorite.color
        : `#${favorite.color}`;
      const hex = hexColor.substring(1);

      // Convert hex to HSL for sliders
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h,
        s,
        l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }

      setHue(Math.round(h * 360));
      setSaturation(Math.round(s * 100));
      setMode("rgb");
      onRgbUpdate && onRgbUpdate(hex);
    } else if (favorite.mode === "temperature") {
      // Handle temperature color
      const temp = parseInt(favorite.color);
      setTemperature(temp);
      setMode("temperature");
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
        target: {
          value: temp,
        },
      };
      onTempUpdate && onTempUpdate(syntheticEvent, temp);
    }
  };

  const getFavoriteColorDisplay = (favorite) => {
    if (favorite.mode === "rgb") {
      return getDisplayColor(favorite.color, null, "rgb");
    } else if (favorite.mode === "temperature") {
      const temp = parseInt(favorite.color);
      return getDisplayColor(null, temp, "temperature");
    }
    return "#ffffff";
  };

  const handleClose = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    onClose();
  };

  // Generate temperature gradient
  const gradientStops = [];
  const steps = 10;
  for (let i = 0; i <= steps; i++) {
    const temp = minTemp + (i / steps) * (maxTemp - minTemp);
    const rgb = kelvinToRGB(temp);
    gradientStops.push(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
  }

  const isRgbActive = mode === "rgb";
  const isTempActive = mode === "temperature";

  // Determine which controls to show based on showControls parameter
  const showHue = showControls === "all" || showControls === "rgb";
  const showSaturation = showControls === "all" || showControls === "rgb";
  const showTemperature =
    showControls === "all" || showControls === "temperature";

  const sliderStyles = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: "calc(100% - 16px)",
    left: "8px",
    "& .MuiSlider-thumb": {
      width: 4,
      height: 20,
      borderRadius: 16,
      backgroundColor: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
      "&:hover, &.Mui-active": {
        boxShadow: "0 1px 4px rgba(0,0,0,0.6)",
      },
      "&:before": {
        display: "none",
      },
    },
    "& .MuiSlider-track": {
      height: 40,
      border: "none",
      borderRadius: 16,
      backgroundColor: "transparent",
    },
    "& .MuiSlider-rail": {
      height: 40,
      borderRadius: 16,
      backgroundColor: "transparent",
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} variant="pops">
      <DialogTitle style={{ margin: 0, padding: spacingToPx(2) }}>
        <Typography
          className="font-bold text-center text-lg"
          style={{
            marginTop: spacingToPx(1),
          }}
        >
          {isTempActive ? `${temperature}K` : "RGB Color"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className="absolute"
          style={{
            right: 8,
            top: 8,
            color: "hsl(var(--muted-foreground))",
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        className="flex flex-col gap-2 overflow-x-hidden w-full"
        style={{
          paddingLeft: spacingToPx(3),
          paddingRight: spacingToPx(3),
          paddingTop: spacingToPx(3),
          paddingBottom: spacingToPx(3),
          boxSizing: "border-box",
        }}
      >
        {/* Hue Slider */}
        {showHue && (
          <div className="w-full" style={{ opacity: isRgbActive ? 1 : 0.4 }}>
            <Typography className="font-bold mb-1 text-sm">Hue</Typography>
            <div className="relative" style={{ height: 40 }}>
              <div
                className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
                  borderRadius: spacingToPx(4),
                }}
              />
              <Slider
                value={hue}
                onChange={handleHueChange}
                onChangeCommitted={handleHueChangeCommitted}
                min={0}
                max={360}
                step={1}
                disabled={disabled}
                sx={sliderStyles}
              />
            </div>
          </div>
        )}

        {/* Saturation Slider */}
        {showSaturation && (
          <div className="w-full" style={{ opacity: isRgbActive ? 1 : 0.4 }}>
            <Typography className="font-bold mb-1 text-sm">
              Saturation
            </Typography>
            <div className="relative" style={{ height: 40 }}>
              <div
                className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none"
                style={{
                  background: `linear-gradient(to right, hsl(${hue}, 0%, 100%) 0%, hsl(${hue}, 100%, 50%) 100%)`,
                  borderRadius: spacingToPx(4),
                }}
              />
              <Slider
                value={saturation}
                onChange={handleSaturationChange}
                onChangeCommitted={handleSaturationChangeCommitted}
                min={0}
                max={100}
                step={1}
                disabled={disabled}
                sx={sliderStyles}
              />
            </div>
          </div>
        )}

        {/* Temperature Slider */}
        {showTemperature && (
          <div className="w-full" style={{ opacity: isTempActive ? 1 : 0.4 }}>
            <Typography className="font-bold mb-1 text-sm">
              Temperature
            </Typography>
            <div className="relative" style={{ height: 40 }}>
              <div
                className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none"
                style={{
                  background: `linear-gradient(to right, ${gradientStops.join(
                    ", "
                  )})`,
                  borderRadius: spacingToPx(4),
                }}
              />
              <Slider
                value={temperature}
                onChange={handleTemperatureChange}
                onChangeCommitted={handleTemperatureChangeCommitted}
                min={minTemp}
                max={maxTemp}
                step={50}
                disabled={disabled}
                sx={sliderStyles}
              />
            </div>
          </div>
        )}

        {/* Favorite Colors */}
        {favoriteColors.length > 0 && (
          <div className="w-full" style={{ marginTop: spacingToPx(1) }}>
            <Typography className="font-bold mb-1 text-sm">
              Recent Colors
            </Typography>
            <div
              className="flex w-full items-center flex-wrap"
              style={{
                gap: spacingToPx(2),
              }}
            >
              {favoriteColors.map((favorite, index) => (
                <div
                  key={`${favorite.color}-${favorite.mode}-${index}`}
                  onClick={() => handleFavoriteColorClick(favorite)}
                  className={`rounded-full transition-transform ${
                    disabled ? "cursor-default" : "cursor-pointer"
                  }`}
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: getFavoriteColorDisplay(favorite),
                    border: "1px solid rgba(192, 192, 192, 0.65)",
                    opacity: disabled ? 0.6 : 1,
                    transition: "transform 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!disabled) {
                      e.currentTarget.style.transform = "scale(1.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!disabled) {
                      e.currentTarget.style.transform = "scale(1)";
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!disabled) {
                      e.currentTarget.style.transform = "scale(0.95)";
                    }
                  }}
                  onMouseUp={(e) => {
                    if (!disabled) {
                      e.currentTarget.style.transform = "scale(1.1)";
                    }
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ColorDialog1;
